import { useState } from 'react'
import { useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import { useRequoteOnSave } from '@/components/quotes/summary/requoteOnSave'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { TravelDistanceItem } from '../../models/user'
import { TravelDistanceFields } from './TravelDistanceFields'
import { useTravelDistanceForm } from './useTravelDistanceForm'

interface EditTravelDistanceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  options: TravelDistanceItem[]
  selected: TravelDistanceItem[] | null
  orgId: string
}

export function EditTravelDistanceDialog({
  open,
  onOpenChange,
  onSuccess,
  options,
  selected,
  orgId,
}: EditTravelDistanceDialogProps) {
  const [loading, setLoading] = useState(false)
  const requote = useRequoteOnSave()
  const editor = useTravelDistanceForm(selected)
  const picked = useWatch({ control: editor.form.control, name: 'travelDistance' })

  function close() {
    editor.discard()
    onOpenChange(false)
  }

  async function handleSubmit() {
    setLoading(true)
    try {
      const result = await editor.save(orgId, { onlyChanged: false })
      if (!result.ok) {
        if (result.error) toast.error('Failed to update driving distance')
        return
      }
      onSuccess()
      if (requote) await requote.run()
      else toast.success('Driving distance updated')
      onOpenChange(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(value) => (value ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit driving distance</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <TravelDistanceFields form={editor.form} options={options} />
        </div>
        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !picked.length}
            loading={loading ? (requote?.pendingLabel ?? 'Saving...') : undefined}
          >
            {requote?.label ?? 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
